/**
 * A ```chart fence: an Apache ECharts option written as JSON, for the forms
 * mermaid cannot draw — heatmaps, scatter and bubble plots, sankey flows,
 * treemaps, sunbursts, radar, gauges, funnels, box plots, calendars, graphs.
 *
 * The option is data, never code: JSON has no functions, and the sanitiser
 * below removes the few option keys that make ECharts fetch a URL or write
 * raw HTML. A chart therefore cannot load anything or run script, even when
 * the agent was steered by a hostile document.
 *
 * @module components/chat/chartSpec
 */

export const CHART_FENCE_LANGUAGES: ReadonlySet<string> = new Set(["chart", "echarts"]);

/** Generous for a chat figure; a body past this is a data dump, not a chart. */
export const MAX_CHART_SOURCE_LENGTH = 256_000;
const MAX_DEPTH = 24;
const MAX_ARRAY_LENGTH = 50_000;

export const DEFAULT_CHART_HEIGHT = 320;
export const MIN_CHART_HEIGHT = 160;
export const MAX_CHART_HEIGHT = 900;

/** Named colour ramps an author can use in place of a colour list. */
export type ChartRampName = "sequential" | "diverging";

export type ChartOption = Record<string, unknown>;

export interface ChartSpec {
  /** Shown above the plot in the interface's own type, like the other figures. */
  readonly title: string | null;
  readonly subtitle: string | null;
  readonly height: number;
  readonly option: ChartOption;
}

export function isChartFenceLanguage(
  language: string,
  stream?: { readonly isStreaming: boolean; readonly code: string },
): boolean {
  const normalized = language.trim().toLowerCase();
  if (CHART_FENCE_LANGUAGES.has(normalized)) return true;
  // A chunk can end inside the opening tag (```cha); hold the figure slot
  // until the tag completes rather than flashing a code block.
  if (stream?.isStreaming && stream.code.trim() === "" && normalized !== "") {
    for (const candidate of CHART_FENCE_LANGUAGES) {
      if (candidate.startsWith(normalized)) return true;
    }
  }
  return false;
}

// Keys whose string value ECharts may load as a resource: `symbol` takes
// `image://<url>`, `image`/`src`/`href`/`url` take a URL, and
// `backgroundColor`/`color` can be `{ image: <url> }` (caught through
// `image`). Any string there is dropped — a relative path is still a
// same-origin request — except a built-in or vector `symbol`.
const BUILT_IN_SYMBOL = /^(?:empty)?(?:circle|rect|roundRect|triangle|diamond|pin|arrow)$|^none$/i;
const DROPPED_KEYS: ReadonlySet<string> = new Set([
  // Toolbox's data view builds HTML from the data and its save button
  // duplicates our own download; neither belongs in a chat figure.
  "toolbox",
  // Title links. (Sankey and graph edges use `links`/`target`, which stay.)
  "link",
  "sublink",
  // Raw CSS on the tooltip element can load `url(…)` resources.
  "extraCssText",
  "className",
  // The tooltip stays inside the figure, rendered by us as HTML.
  "appendTo",
  "appendToBody",
  "renderMode",
  // A dataset filter's `reg` is compiled to a RegExp and run over every
  // row; a backtracking pattern could freeze the tab. Filters keep their
  // other comparisons.
  "reg",
]);
const RESOURCE_KEYS: ReadonlySet<string> = new Set(["image", "symbol", "src", "href", "url"]);

function isSafeResourceString(key: string, value: string): boolean {
  if (key !== "symbol") return false;
  const symbol = value.trim();
  return BUILT_IN_SYMBOL.test(symbol) || /^path:\/\/[\d\s.,+\-a-z]*$/i.test(symbol);
}
// Allowed inline markup in tooltip templates: line breaks and emphasis only.
const SAFE_TAG = /^<\/?(?:br|b|strong|i|em)\s*\/?>$/i;

/**
 * Tooltip templates are inserted as HTML. Keep `{b}: {c}<br/>` style
 * templates working and escape every other tag, so an injected
 * `<img onerror>` reads as text instead of running.
 */
export function sanitizeFormatterTemplate(template: string): string {
  return template.replace(/<[^>]*>?/g, (tag) =>
    SAFE_TAG.test(tag) ? tag : tag.replace(/</g, "&lt;").replace(/>/g, "&gt;"),
  );
}

function sanitizeValue(value: unknown, key: string | null, depth: number): unknown {
  if (depth > MAX_DEPTH) return undefined;
  if (typeof value === "string") {
    if (key === "formatter") return sanitizeFormatterTemplate(value);
    if (key !== null && RESOURCE_KEYS.has(key)) return isSafeResourceString(key, value) ? value : undefined;
    // A size must be a number (or a pair); a template such as "{c}" makes
    // ECharts drop the whole series silently.
    if (key === "symbolSize") return undefined;
    return value;
  }
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (value === null || typeof value === "boolean") return value;
  if (Array.isArray(value)) {
    return value
      .slice(0, MAX_ARRAY_LENGTH)
      .map((entry) => sanitizeValue(entry, key, depth + 1))
      .filter((entry) => entry !== undefined);
  }
  if (typeof value === "object") {
    const result: Record<string, unknown> = {};
    for (const [childKey, child] of Object.entries(value)) {
      if (DROPPED_KEYS.has(childKey) || childKey === "__proto__") continue;
      const clean = sanitizeValue(child, childKey, depth + 1);
      if (clean !== undefined) result[childKey] = clean;
    }
    return result;
  }
  return undefined;
}

/** Removes everything that could fetch, navigate or inject markup. */
export function sanitizeChartOption(option: ChartOption): ChartOption {
  return (sanitizeValue(option, null, 0) as ChartOption | undefined) ?? {};
}

function readText(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function clampHeight(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return DEFAULT_CHART_HEIGHT;
  return Math.min(MAX_CHART_HEIGHT, Math.max(MIN_CHART_HEIGHT, Math.round(value)));
}

/**
 * Series ECharts can draw from JSON alone. `map` needs geography we do not
 * register and `custom` needs a render function JSON cannot carry; an unknown
 * type is ignored by ECharts, which would leave an empty plot.
 */
export const CHART_SERIES_TYPES: ReadonlySet<string> = new Set([
  "bar",
  "boxplot",
  "candlestick",
  "chord",
  "effectScatter",
  "funnel",
  "gauge",
  "graph",
  "heatmap",
  "line",
  "lines",
  "parallel",
  "pictorialBar",
  "pie",
  "radar",
  "sankey",
  "scatter",
  "sunburst",
  "themeRiver",
  "tree",
  "treemap",
]);

function isDrawableSeries(entry: unknown): boolean {
  return (
    entry !== null &&
    typeof entry === "object" &&
    !Array.isArray(entry) &&
    CHART_SERIES_TYPES.has(String((entry as Record<string, unknown>).type))
  );
}

/** At least one series, and every series is a form we can draw. */
function hasDrawableSeries(option: ChartOption): boolean {
  const series = option.series;
  if (Array.isArray(series)) return series.length > 0 && series.every(isDrawableSeries);
  return isDrawableSeries(series);
}

/**
 * The spec for a fence body, or null when it is not (yet) a chart: still
 * streaming, not JSON, or an option with nothing to draw. A single
 * `title` object moves out of the canvas into the figure header so charts
 * share the type of the rest of the reply; `height` is ours, not ECharts'.
 */
export function parseChartSpec(source: string): ChartSpec | null {
  const trimmed = source.trim();
  if (trimmed.length === 0 || trimmed.length > MAX_CHART_SOURCE_LENGTH) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return null;
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) return null;
  const option = sanitizeChartOption(parsed as ChartOption);
  if (!hasDrawableSeries(option)) return null;

  const { height, title: rawTitle, ...rest } = option;
  let title: string | null = null;
  let subtitle: string | null = null;
  let drawn: ChartOption = rest;
  if (typeof rawTitle === "string") {
    title = readText(rawTitle);
  } else if (rawTitle !== null && typeof rawTitle === "object" && !Array.isArray(rawTitle)) {
    const titleObject = rawTitle as Record<string, unknown>;
    title = readText(titleObject.text);
    subtitle = readText(titleObject.subtext);
  } else if (Array.isArray(rawTitle)) {
    // Several titles label several plots (a pie per region); they stay in
    // the canvas where their positions mean something.
    drawn = { ...rest, title: rawTitle };
  }
  return { title, subtitle, height: clampHeight(height), option: drawn };
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasComponent(option: ChartOption, key: string): boolean {
  const value = option[key];
  return Array.isArray(value) ? value.length > 0 : isPlainObject(value);
}

function isUnpositioned(component: Record<string, unknown>): boolean {
  return ["left", "right", "top", "bottom"].every((side) => component[side] === undefined);
}

/**
 * Chat-column defaults the author did not set: a tooltip on every chart (the
 * hover layer is the point of an interactive figure), a legend in the top
 * left like the other figures, a tight grid that keeps axis labels inside
 * the plot instead of ECharts' wide report margins, and screen-reader
 * descriptions. Anything the option sets itself wins.
 */
export function applyChartDefaults(option: ChartOption): ChartOption {
  const next: ChartOption = { aria: { enabled: true }, ...option };
  const cartesian = hasComponent(option, "xAxis") || hasComponent(option, "yAxis");
  const legend = option.legend;
  const hasLegend = Array.isArray(legend) ? legend.length > 0 : isPlainObject(legend);
  if (isPlainObject(legend) && isUnpositioned(legend)) {
    next.legend = { type: "scroll", left: 0, top: 0, ...legend };
  }
  if (option.tooltip === undefined) {
    // A crosshair suits lines and bars; a heatmap cell or scatter dot is
    // read one mark at a time.
    const series = Array.isArray(option.series) ? option.series : [option.series];
    const axisTrigger =
      cartesian &&
      series.every((entry) => isPlainObject(entry) && (entry.type === "line" || entry.type === "bar"));
    next.tooltip = { trigger: axisTrigger ? "axis" : "item", confine: true };
  } else if (isPlainObject(option.tooltip) && option.tooltip.confine === undefined) {
    next.tooltip = { confine: true, ...option.tooltip };
  }
  if (cartesian && option.grid === undefined) {
    const dataZoom = option.dataZoom;
    const zoomEntries = Array.isArray(dataZoom) ? dataZoom : dataZoom === undefined ? [] : [dataZoom];
    const slider = zoomEntries.some((entry) => isPlainObject(entry) && entry.type === "slider");
    const visualMap = option.visualMap;
    const bottomVisualMap =
      isPlainObject(visualMap) && visualMap.orient === "horizontal" && visualMap.top === undefined;
    next.grid = {
      left: 4,
      right: 12,
      top: hasLegend ? 36 : 12,
      bottom: slider ? 48 : bottomVisualMap ? 56 : 4,
      outerBoundsMode: "same",
    };
  }
  return next;
}

/** The ramp an author named, e.g. `"inRange": { "color": "sequential" }`. */
export function readChartRampName(value: unknown): ChartRampName | null {
  return value === "sequential" || value === "diverging" ? value : null;
}

/**
 * Replaces named ramps with the current theme's colours, so a heatmap
 * written once reads correctly in light and dark.
 */
export function resolveChartRamps(
  option: ChartOption,
  ramps: Readonly<Record<ChartRampName, ReadonlyArray<string>>>,
): ChartOption {
  const visit = (value: unknown, key: string | null): unknown => {
    if (key === "color") {
      const ramp = readChartRampName(value);
      if (ramp) return [...ramps[ramp]];
    }
    if (Array.isArray(value)) return value.map((entry) => visit(entry, null));
    if (value !== null && typeof value === "object") {
      return Object.fromEntries(
        Object.entries(value).map(([childKey, child]) => [childKey, visit(child, childKey)]),
      );
    }
    return value;
  };
  return visit(option, null) as ChartOption;
}
